import {
  createNewConversation,
  postUserMessage,
} from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { convertToChatMessage } from "@mirage/service-conversation/utils/convertToChatMessage";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { ChatConversationMessage } from "@mirage/mosaics/Chat/types";
import type {
  ConversationMessage,
  ConversationSuggestedQuery,
} from "@mirage/service-conversation/types";
import { convertSuggestionSourceToDocumentInfo } from "@mirage/service-conversation/utils/convertSuggestionQueryToDocumentInfo";
import { createConversationClientContext } from "@mirage/service-conversation/utils/createConversationClientContext";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { atom, useAtom } from "jotai";

/**
 * This hook is used to initiate new conversation on each new query.
 */

export const activeConversationIdAtom = atom<string>();

export const useInitialConversation = (
  query: string,
  isEnabled: boolean,
  isSerpAnswerConversation = false,
  // This is taken as a dependency because the value of the previousPath hook
  // has already been updated to the current page by the time this component is rendered.
  previousPathSurface?: DashActionSurface,
) => {
  const [conversationId, setConversationId] = useState<string>();
  const [, setActiveConversationId] = useAtom(activeConversationIdAtom);

  const hideIrrelevantAnswers =
    useFeatureFlagValue("dash_web_2025_05_08_hide_irrelevant_answers") === "ON";

  const conversation = useConversation(conversationId);

  const composeMessages = useMemo(
    () => conversation?.messages?.map(convertToChatMessage) || [],
    [conversation?.messages],
  );

  const isWaitingForResponse = useMemo(() => {
    return (
      conversation === undefined ||
      conversation.messages?.length === 0 ||
      conversation.messages?.some(
        (message) =>
          message.status === "pending" || message.status === "streaming",
      )
    );
  }, [conversation]);

  useEffect(() => {
    setActiveConversationId(conversationId);
  }, [conversationId, setActiveConversationId]);

  const startNewConversation = async (query: string) => {
    // If this is the first conversation of this search session, the originating surface
    // should be the previous path surface (if available). If a new conversation is triggered
    // from SERP, or there is no previous path, the originating surface defaults to "serp".
    const originatingSurface = conversationId
      ? "serp"
      : previousPathSurface || "serp";

    const newConversation = await createNewConversation();
    setConversationId(newConversation.id);

    postUserMessage({
      conversationId: newConversation.id,
      message: query,
      source: `${originatingSurface}-omnibox`,
      clientContext: isSerpAnswerConversation
        ? createConversationClientContext("serp-answer")
        : undefined,
    });
  };

  const postFollowUpMessage = useCallback(
    async (query: ConversationSuggestedQuery) => {
      if (conversationId) {
        postUserMessage({
          conversationId,
          message: query.text,
          attachments: query.docIds?.map(convertSuggestionSourceToDocumentInfo),
          source: "serp-compose_chat_pane",
          clientContext: isSerpAnswerConversation
            ? createConversationClientContext("serp-follow-up")
            : undefined,
        });
      }
    },
    [conversationId],
  );

  // If flag is on, initiate a new conversation on each new query (unless filters are enabled)
  useEffect(() => {
    if (isEnabled && query) {
      startNewConversation(query);
    }
  }, [query, isEnabled]);

  const loadingState: LoadingState = useMemo(() => {
    if (!isEnabled) {
      return "done";
    }
    return getLoadingState(conversation?.messages || [], hideIrrelevantAnswers);
  }, [isEnabled, conversation?.messages, hideIrrelevantAnswers]);

  return {
    messages: composeMessages,
    suggestedQueries: conversation?.suggestedQueries || [],
    conversationId,
    isWaitingForResponse,
    postFollowUpMessage,
    loadingState,
  };
};

function getLoadingState(
  messages: ConversationMessage[],
  hideIrrelevantAnswers: boolean,
): LoadingState {
  if (messages.length === 0) {
    return "initiating";
  }
  // Look for an initial response -- first message from Assistant
  const firstAssistantMessage = messages.find(
    (message) => message.messageType.case === "assistantMessage",
  );
  if (!firstAssistantMessage) {
    return "initiating"; // waiting for assistant message
  }
  if (hideIrrelevantAnswers) {
    const convText =
      firstAssistantMessage.messageType.case === "assistantMessage"
        ? firstAssistantMessage.messageType.value.text
        : "";
    if (
      convText.includes("I couldn't find") ||
      convText.includes("I could not find")
    ) {
      return "not_found";
    }
  }
  switch (firstAssistantMessage.status) {
    case "pending":
      return "initiating";
    case "streaming":
      return "streaming";
    case "success":
      return "done";
    case "error":
      return "error";
    default:
      firstAssistantMessage.status satisfies never;
      throw new Error(
        `Unknown message status: ${firstAssistantMessage.status}`,
      );
  }
}

export type LoadingState =
  | "initiating"
  | "streaming"
  | "done"
  | "error"
  | "not_found";

export type AnswerConversationProps =
  | {
      isEnabled: true;
      messages: ChatConversationMessage[]; // empty when loading
      suggestedQueries: ConversationSuggestedQuery[];
      conversationId: string | undefined; // undefined when loading
      /**
       * Post a follow up message to the conversation
       * @returns A promise that resolves when the message is posted (does NOT wait for its response)
       */
      postFollowUpMessage: (query: ConversationSuggestedQuery) => Promise<void>;
      loadingState: LoadingState;
    }
  | {
      isEnabled: false;
    };

export function useAnswerConversationProps(
  isEnabled: boolean,
  initialConversation: ReturnType<typeof useInitialConversation>,
): AnswerConversationProps {
  return useMemo(() => {
    if (isEnabled) {
      return {
        ...initialConversation,
        isEnabled,
      };
    }
    return {
      isEnabled: false,
    };
  }, [initialConversation, isEnabled]);
}
